import React, { useEffect, useState } from 'react';
import { Plus, Eye, Pencil } from 'lucide-react';
import { useForm, useFieldArray } from 'react-hook-form';
import DataTable from '../../components/DataTable';
import { Modal, Button, FormField, Input, Select, Textarea, PageHeader, Badge } from '../../components/UI';
import { useCRUD } from '../../hooks/useCRUD';
import { prescriptionService, patientService, employeeService, medicineService } from '../../api/services';
import { toJalali } from '../../utils/jalali';
import { getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

export default function PrescriptionsPage() {
  const crud = useCRUD(prescriptionService, 'Prescription');
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [medicines, setMedicines] = useState([]);
  const { register, handleSubmit, reset, control, formState: { errors } } = useForm({
    defaultValues: { medicines: [{ medicineName: '', dosage: '', frequency: '', duration: '' }] }
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'medicines' });

  useEffect(() => {
    crud.fetchData();
    patientService.getAll({ limit: 100 }).then(r => setPatients(r.data.data || []));
    employeeService.getAll({ limit: 100 }).then(r => setDoctors(r.data.data || []));
    medicineService.getAll({ limit: 100 }).then(r => setMedicines(r.data.data || []));
  }, [crud.page, crud.search]);

  useEffect(() => {
    if (crud.editItem) reset(crud.editItem);
    else reset({ patient: '', doctor: '', diagnosis: '', notes: '', medicines: [{ medicineName: '', dosage: '', frequency: '', duration: '' }] });
  }, [crud.editItem]);

  const onSubmit = (data) => crud.handleSave(data);

  const columns = [
    { key: 'prescriptionNumber', label: 'Rx No.', sortable: true },
    { key: 'patient', label: 'Patient', render: (v) => v?.name || '-' },
    { key: 'doctor', label: 'Doctor', render: (v) => v?.name || '-' },
    { key: 'diagnosis', label: 'Diagnosis' },
    { key: 'medicines', label: 'Medicines', render: (v) => `${v?.length || 0} item(s)` },
    { key: 'status', label: 'Status', render: (v) => <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(v)}`}>{v}</span> },
    { key: 'createdAt', label: 'Date', render: (v) => toJalali(v) },
  ];

  return (
    <div>
      <PageHeader
        title="Prescriptions"
        subtitle="Manage patient prescriptions"
        actions={<Button onClick={crud.openCreate}><Plus size={16} /> New Prescription</Button>}
      />
      <DataTable
        columns={columns}
        data={crud.data}
        loading={crud.loading}
        totalItems={crud.total}
        page={crud.page}
        pageSize={20}
        onPageChange={crud.setPage}
        onSearch={crud.setSearch}
        title="Prescriptions"
        exportFilename="prescriptions"
        actions={(row) => (
          <button onClick={() => crud.openEdit(row)} className="p-1.5 rounded hover:bg-blue-50 text-gray-400 hover:text-blue-600">
            <Pencil size={15} />
          </button>
        )}
      />

      <Modal isOpen={crud.modalOpen} onClose={crud.closeModal} title={crud.editItem ? 'Edit Prescription' : 'New Prescription'} size="xl">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Patient" required error={errors.patient?.message}>
              <Select {...register('patient', { required: 'Required' })}>
                <option value="">Select patient</option>
                {patients.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
              </Select>
            </FormField>
            <FormField label="Doctor">
              <Select {...register('doctor')}>
                <option value="">Select doctor</option>
                {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
              </Select>
            </FormField>
          </div>
          <FormField label="Diagnosis">
            <Input {...register('diagnosis')} placeholder="Diagnosis" />
          </FormField>

          {/* Medicines list */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">Medicines</label>
              <Button type="button" variant="outline" size="sm" onClick={() => append({ medicineName: '', dosage: '', frequency: '', duration: '' })}>
                <Plus size={14} /> Add
              </Button>
            </div>
            <div className="space-y-2">
              {fields.map((field, idx) => (
                <div key={field.id} className="grid grid-cols-4 gap-2 p-3 bg-gray-50 rounded-lg">
                  <Input {...register(`medicines.${idx}.medicineName`)} placeholder="Medicine name" />
                  <Input {...register(`medicines.${idx}.dosage`)} placeholder="Dosage (e.g. 500mg)" />
                  <Input {...register(`medicines.${idx}.frequency`)} placeholder="Frequency (e.g. 3x/day)" />
                  <div className="flex gap-2">
                    <Input {...register(`medicines.${idx}.duration`)} placeholder="Duration" />
                    {fields.length > 1 && (
                      <button type="button" onClick={() => remove(idx)} className="text-red-400 hover:text-red-600 px-2">✕</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <FormField label="Notes">
            <Textarea {...register('notes')} placeholder="Additional notes" />
          </FormField>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={crud.closeModal}>Cancel</Button>
            <Button type="submit" loading={crud.saving}>Save</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
